import { useEffect, useState, type ComponentType } from 'react';
import {
  SIMULATION_ENVIRONMENTS,
  SIMULATION_SKIES,
  SIMULATION_THREAT_APPROACHES,
  SIMULATION_THREAT_TRAJECTORIES,
  SIMULATION_THREAT_TYPES,
  SIMULATION_TIMES_OF_DAY,
  roleAtLeast,
  type CreateSimulationBatchInput,
  type ExtractedImage,
  type Label,
  type OrgRole,
  type SimulationAssistReply,
  type SimulationBatch,
  type SimulationEnvironment,
  type SimulationEnvironmentSource,
  type SimulationMediaKind,
  type SimulationScenario,
  type SimulationSky,
  type SimulationStatus,
  type SimulationThreat,
  type SimulationThreatApproach,
  type SimulationThreatTrajectory,
  type SimulationThreatType,
  type SimulationTimeOfDay,
  type VideoListItem,
} from '@ddp/shared';
import Button from '@carbon/react/es/components/Button/Button';
import TextInput from '@carbon/react/es/components/TextInput/TextInput';
import Select from '@carbon/react/es/components/Select/Select';
import SelectItem from '@carbon/react/es/components/SelectItem/SelectItem';
import { NumberInput } from '@carbon/react/es/components/NumberInput/NumberInput';
import Checkbox from '@carbon/react/es/components/Checkbox/Checkbox';
import TextArea from '@carbon/react/es/components/TextArea/TextArea';
import Modal from '@carbon/react/es/components/Modal/Modal';
import { InlineNotification } from '@carbon/react/es/components/Notification/Notification';
import Add from '@carbon/icons-react/es/Add';
import Close from '@carbon/icons-react/es/Close';
import AiGenerate from '@carbon/icons-react/es/AiGenerate';
import Play from '@carbon/icons-react/es/Play';
import Checkmark from '@carbon/icons-react/es/Checkmark';
import CloseOutline from '@carbon/icons-react/es/CloseOutline';
import TrashCan from '@carbon/icons-react/es/TrashCan';
import Send from '@carbon/icons-react/es/Send';

import { ApiError, apiFetch } from '../../api/client.js';
import { useAuthStore } from '../../auth/store.js';
import { useT, type MessageKey } from '../../i18n/index.js';
import { HelpHint } from '../../components/HelpHint.js';
import { useAssistant } from '../assistant/use-assistant.js';
import { BLANK_SCENARIO, BLANK_THREAT, SIMULATION_TEMPLATES, THREAT_PRESETS } from './templates.js';
import { ScenarioPreview } from './ScenarioPreview.js';
import { BackdropPicker } from './BackdropPicker.js';

/** Libellés i18n des valeurs d'énumération, pour peupler les sélecteurs. */
const ENV_LABEL: Record<SimulationEnvironment, MessageKey> = {
  FIELD: 'sim.env.field',
  FOREST: 'sim.env.forest',
  DENSE_HABITAT: 'sim.env.denseHabitat',
  PERIURBAN: 'sim.env.periurban',
};
const TIME_LABEL: Record<SimulationTimeOfDay, MessageKey> = {
  DAWN: 'sim.time.dawn',
  DAY: 'sim.time.day',
  DUSK: 'sim.time.dusk',
  NIGHT: 'sim.time.night',
};
const SKY_LABEL: Record<SimulationSky, MessageKey> = {
  CLEAR: 'sim.sky.clear',
  PARTLY_CLOUDY: 'sim.sky.partlyCloudy',
  OVERCAST: 'sim.sky.overcast',
  FOG: 'sim.sky.fog',
};
const THREAT_TYPE_LABEL: Record<SimulationThreatType, MessageKey> = {
  FPV: 'sim.threatType.fpv',
  FIXED_WING: 'sim.threatType.fixedWing',
  QUAD: 'sim.threatType.quad',
  VTOL: 'sim.threatType.vtol',
};
const THREAT_TRAJ_LABEL: Record<SimulationThreatTrajectory, MessageKey> = {
  DIVE: 'sim.traj.dive',
  PASS: 'sim.traj.pass',
  ORBIT: 'sim.traj.orbit',
  DISPERSE: 'sim.traj.disperse',
  HOVER: 'sim.traj.hover',
};
const THREAT_APPROACH_LABEL: Record<SimulationThreatApproach, MessageKey> = {
  FRONTAL: 'sim.approach.frontal',
  LATERAL: 'sim.approach.lateral',
  VERTICAL: 'sim.approach.vertical',
  MIXED: 'sim.approach.mixed',
};
const STATUS_META: Record<SimulationStatus, { cls: string; key: MessageKey }> = {
  DRAFT: { cls: 'draft', key: 'sim.status.draft' },
  QUEUED: { cls: 'queued', key: 'sim.status.queued' },
  RENDERING: { cls: 'render', key: 'sim.status.rendering' },
  READY: { cls: 'ready', key: 'sim.status.ready' },
  APPROVED: { cls: 'approved', key: 'sim.status.approved' },
  REJECTED: { cls: 'rejected', key: 'sim.status.rejected' },
  FAILED: { cls: 'failed', key: 'sim.status.failed' },
};

/** Lit une valeur numérique bornée depuis un champ, en retombant sur `fallback` si illisible. */
function clampNum(raw: string, min: number, max: number, fallback: number): number {
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

/**
 * Section « Simulation » de la page Data (ADR-0021, atelier deux volets). À gauche, le constructeur
 * (templates + assistant + formulaire) ; à droite, un récapitulatif vivant et le lancement. Créer un
 * scénario est toujours possible ; le rendu est une capacité **opt-in** (dormante) — un lancement
 * refusé (`503`) est affiché honnêtement.
 */
export function SimulationSection() {
  const t = useT();
  const role = useAuthStore((s) => s.currentUser?.memberships[0]?.role ?? null);
  const canEdit = role !== null && roleAtLeast(role as OrgRole, 'EDITOR');

  const [batches, setBatches] = useState<SimulationBatch[]>([]);
  const [labels, setLabels] = useState<Label[]>([]);
  // Médias référençables pour la reconstruction (ADR-0023, incrément C) — cloisonnés côté API.
  const [videos, setVideos] = useState<VideoListItem[]>([]);
  const [images, setImages] = useState<ExtractedImage[]>([]);
  const [mediaFilter, setMediaFilter] = useState<'ALL' | SimulationMediaKind>('ALL');
  const [mediaSearch, setMediaSearch] = useState('');
  const [name, setName] = useState('');
  const [scenario, setScenario] = useState<SimulationScenario>(BLANK_SCENARIO);
  const [activeTemplate, setActiveTemplate] = useState<string | null>(null);
  const [newTarget, setNewTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dormant, setDormant] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  // Bump pour forcer l'aperçu à relire l'URL du fond après un changement (ADR-0033).
  const [backdropRefresh, setBackdropRefresh] = useState(0);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const [b, l, v, im] = await Promise.all([
          apiFetch<SimulationBatch[]>('/simulation/batches'),
          apiFetch<Label[]>('/labels').catch(() => [] as Label[]),
          apiFetch<VideoListItem[]>('/videos').catch(() => [] as VideoListItem[]),
          apiFetch<ExtractedImage[]>('/images').catch(() => [] as ExtractedImage[]),
        ]);
        if (!active) return;
        setBatches(b);
        setLabels(l);
        setVideos(v);
        setImages(im);
      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : 'Chargement impossible');
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const applyTemplate = (id: string, s: SimulationScenario, label: string) => {
    setActiveTemplate(id);
    setScenario({
      ...s,
      targets: s.targets.map((tg) => ({ ...tg })),
      threats: s.threats.map((th) => ({ ...th })),
    });
    if (!name.trim()) setName(label);
  };

  const patch = (p: Partial<SimulationScenario>) => setScenario((s) => ({ ...s, ...p }));

  const addTarget = (labelName: string) => {
    const clean = labelName.trim();
    if (!clean) return;
    if (scenario.targets.some((tg) => tg.label.toLowerCase() === clean.toLowerCase())) return;
    patch({ targets: [...scenario.targets, { label: clean, density: 1 }] });
    setNewTarget('');
  };
  const removeTarget = (labelName: string) =>
    patch({ targets: scenario.targets.filter((tg) => tg.label !== labelName) });

  // Menaces aériennes (ADR-0023) : ajout depuis un preset ou vierge, édition et retrait par index.
  const addThreat = (th: SimulationThreat) => patch({ threats: [...scenario.threats, { ...th }] });
  const removeThreat = (idx: number) =>
    patch({ threats: scenario.threats.filter((_, i) => i !== idx) });
  const patchThreat = (idx: number, p: Partial<SimulationThreat>) =>
    patch({ threats: scenario.threats.map((th, i) => (i === idx ? { ...th, ...p } : th)) });

  // Source d'environnement et médias de référence (ADR-0023, incrément C).
  const setSource = (src: SimulationEnvironmentSource) => patch({ environmentSource: src });
  const isMediaSelected = (kind: SimulationMediaKind, id: string) =>
    scenario.referenceMedia.some((m) => m.kind === kind && m.id === id);
  const toggleMedia = (kind: SimulationMediaKind, id: string) =>
    patch({
      referenceMedia: isMediaSelected(kind, id)
        ? scenario.referenceMedia.filter((m) => !(m.kind === kind && m.id === id))
        : [...scenario.referenceMedia, { kind, id }],
    });

  const mediaOptions: { kind: SimulationMediaKind; id: string; name: string; sub: string }[] = [
    ...videos.map((v) => ({ kind: 'VIDEO' as const, id: v.id, name: v.title, sub: v.filename })),
    ...images.map((im) => ({
      kind: 'IMAGE' as const,
      id: im.id,
      name: im.videoTitle,
      sub: `${(im.timestampMs / 1000).toFixed(1)} s`,
    })),
  ];
  const shownMedia = mediaOptions.filter(
    (m) =>
      (mediaFilter === 'ALL' || m.kind === mediaFilter) &&
      (mediaSearch.trim() === '' ||
        `${m.name} ${m.sub}`.toLowerCase().includes(mediaSearch.trim().toLowerCase())),
  );
  const reconstruction = scenario.environmentSource === 'RECONSTRUCTION';

  const canCreate =
    canEdit &&
    name.trim().length > 0 &&
    scenario.targets.length + scenario.threats.length > 0 &&
    (scenario.outputs.labeledImages || scenario.outputs.video) &&
    (!reconstruction || scenario.referenceMedia.length > 0);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const body: CreateSimulationBatchInput = { name: name.trim(), scenario };
      const created = await apiFetch<SimulationBatch>('/simulation/batches', {
        method: 'POST',
        body,
      });
      setBatches((prev) => [created, ...prev]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Création impossible');
    } finally {
      setBusy(false);
    }
  };

  const launch = async (id: string) => {
    setError(null);
    setDormant(false);
    try {
      const updated = await apiFetch<SimulationBatch>(`/simulation/batches/${id}/launch`, {
        method: 'POST',
      });
      setBatches((prev) => prev.map((b) => (b.id === id ? updated : b)));
    } catch (err) {
      // Rendu dormant : le gate répond 503. On l'affiche comme une info, pas une erreur.
      if (err instanceof ApiError && err.status === 503) setDormant(true);
      else setError(err instanceof Error ? err.message : 'Lancement impossible');
    }
  };

  const remove = async (id: string) => {
    try {
      await apiFetch<void>(`/simulation/batches/${id}`, { method: 'DELETE' });
      setBatches((prev) => prev.filter((b) => b.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Suppression impossible');
    }
  };

  // Validation humaine (ADR-0023) : un lot rendu n'alimente l'entraînement qu'une fois approuvé.
  const approve = async (id: string) => {
    setError(null);
    try {
      const updated = await apiFetch<SimulationBatch>(`/simulation/batches/${id}/approve`, {
        method: 'POST',
      });
      setBatches((prev) => prev.map((b) => (b.id === id ? updated : b)));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('sim.review.approveError'));
    }
  };

  // Le rejet exige un motif : on passe par une petite fenêtre plutôt qu'un rejet muet.
  const [rejecting, setRejecting] = useState<{ id: string; name: string } | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectBusy, setRejectBusy] = useState(false);

  const submitReject = async () => {
    if (!rejecting || rejectReason.trim().length === 0) return;
    setRejectBusy(true);
    setError(null);
    try {
      const updated = await apiFetch<SimulationBatch>(
        `/simulation/batches/${rejecting.id}/reject`,
        { method: 'POST', body: { reason: rejectReason.trim() } },
      );
      setBatches((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
      setRejecting(null);
      setRejectReason('');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('sim.review.rejectError'));
    } finally {
      setRejectBusy(false);
    }
  };

  return (
    <section className="ddp-sim" id="simulation" aria-label={t('sim.title')}>
      <div className="ddp-sim__head">
        <h2 className="ddp-data__section-title">
          {t('sim.title')}
          <HelpHint textKey="help.simulation.what" />
        </h2>
      </div>
      <p className="ddp-sim__lead">{t('sim.lead')}</p>

      <div className="ddp-sim__dormant">
        <span>{t('sim.dormantNote')}</span>
      </div>

      {error && (
        <InlineNotification
          kind="error"
          title={t('common.error')}
          subtitle={error}
          lowContrast
          onCloseButtonClick={() => setError(null)}
        />
      )}
      {dormant && (
        <InlineNotification
          kind="info"
          title={t('sim.launchDormantTitle')}
          subtitle={t('sim.launchDormantBody')}
          lowContrast
          onCloseButtonClick={() => setDormant(false)}
        />
      )}

      {canEdit && (
        <div className="ddp-sim__atelier">
          {/* --- Volet gauche : constructeur --- */}
          <div className="ddp-sim__builder">
            <div className="ddp-sim__starters">
              <span className="ddp-sim__starters-label">{t('sim.startWith')}</span>
              {SIMULATION_TEMPLATES.map((tpl) => {
                const label = t(tpl.nameKey);
                return (
                  <button
                    key={tpl.id}
                    type="button"
                    className={`ddp-sim__chip${activeTemplate === tpl.id ? ' ddp-sim__chip--on' : ''}`}
                    onClick={() => applyTemplate(tpl.id, tpl.scenario, label)}
                  >
                    {label}
                  </button>
                );
              })}
              <button
                type="button"
                className="ddp-sim__assist-btn"
                onClick={() => setAssistantOpen((v) => !v)}
              >
                <AiGenerate size={16} />
                {t('sim.describeToAssistant')}
              </button>
            </div>

            {assistantOpen && (
              <ScenarioAssistant
                onApply={(s) => {
                  setScenario({ ...s, targets: s.targets.map((tg) => ({ ...tg })) });
                  setActiveTemplate(null);
                }}
              />
            )}

            <div className="ddp-sim__form">
              <TextInput
                id="sim-name"
                labelText={t('sim.field.name')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('sim.field.namePlaceholder')}
              />

              {/* Source d'environnement (ADR-0023, incrément C) : preset générique ou reconstruction
                  depuis les médias de la zone. */}
              <div className="ddp-sim__envsrc">
                <span className="ddp-sim__label">{t('sim.field.envSource')}</span>
                <div className="ddp-sim__seg" role="group" aria-label={t('sim.field.envSource')}>
                  <button
                    type="button"
                    className={!reconstruction ? 'is-on' : ''}
                    aria-pressed={!reconstruction}
                    onClick={() => setSource('PRESET')}
                  >
                    {t('sim.envSource.preset')}
                  </button>
                  <button
                    type="button"
                    className={reconstruction ? 'is-on' : ''}
                    aria-pressed={reconstruction}
                    onClick={() => setSource('RECONSTRUCTION')}
                  >
                    {t('sim.envSource.reconstruction')}
                  </button>
                </div>
              </div>

              <div className="ddp-sim__grid">
                <Select
                  id="sim-env"
                  labelText={t('sim.field.environment')}
                  value={scenario.environment}
                  onChange={(e) => patch({ environment: e.target.value as SimulationEnvironment })}
                >
                  {SIMULATION_ENVIRONMENTS.map((v) => (
                    <SelectItem key={v} value={v} text={t(ENV_LABEL[v])} />
                  ))}
                </Select>
                <Select
                  id="sim-time"
                  labelText={t('sim.field.timeOfDay')}
                  value={scenario.timeOfDay}
                  onChange={(e) => patch({ timeOfDay: e.target.value as SimulationTimeOfDay })}
                >
                  {SIMULATION_TIMES_OF_DAY.map((v) => (
                    <SelectItem key={v} value={v} text={t(TIME_LABEL[v])} />
                  ))}
                </Select>
                <Select
                  id="sim-sky"
                  labelText={t('sim.field.sky')}
                  value={scenario.sky}
                  onChange={(e) => patch({ sky: e.target.value as SimulationSky })}
                >
                  {SIMULATION_SKIES.map((v) => (
                    <SelectItem key={v} value={v} text={t(SKY_LABEL[v])} />
                  ))}
                </Select>
                <NumberInput
                  id="sim-alt"
                  label={t('sim.field.altitude')}
                  min={5}
                  max={400}
                  value={scenario.altitudeM}
                  onChange={(_e, { value }) =>
                    patch({ altitudeM: clampNum(String(value), 5, 400, scenario.altitudeM) })
                  }
                />
                <NumberInput
                  id="sim-pitch"
                  label={t('sim.field.gimbal')}
                  min={0}
                  max={90}
                  value={scenario.gimbalPitchDeg}
                  onChange={(_e, { value }) =>
                    patch({
                      gimbalPitchDeg: clampNum(String(value), 0, 90, scenario.gimbalPitchDeg),
                    })
                  }
                />
                <NumberInput
                  id="sim-count"
                  label={t('sim.field.imageCount')}
                  min={1}
                  max={2000}
                  value={scenario.imageCount}
                  onChange={(_e, { value }) =>
                    patch({ imageCount: clampNum(String(value), 1, 2000, scenario.imageCount) })
                  }
                />
              </div>

              {/* Médias de référence pour la reconstruction (ADR-0023, incrément C). On référence des
                  vidéos/images déjà sur la plateforme (cloisonnées) ; l'octet ne traverse pas l'API. */}
              {reconstruction && (
                <div className="ddp-sim__media">
                  <span className="ddp-sim__label">
                    {t('sim.field.referenceMedia')}
                    <span className="ddp-sim__dormant-tag">{t('sim.media.dormant')}</span>
                  </span>
                  <div className="ddp-sim__media-filters">
                    <input
                      className="ddp-sim__target-input"
                      value={mediaSearch}
                      placeholder={t('sim.media.filter')}
                      onChange={(e) => setMediaSearch(e.target.value)}
                    />
                    {(['ALL', 'VIDEO', 'IMAGE'] as const).map((f) => (
                      <button
                        key={f}
                        type="button"
                        className={`ddp-sim__mchip${mediaFilter === f ? ' is-on' : ''}`}
                        onClick={() => setMediaFilter(f)}
                      >
                        {t(
                          f === 'ALL'
                            ? 'sim.media.all'
                            : f === 'VIDEO'
                              ? 'sim.media.videos'
                              : 'sim.media.images',
                        )}
                      </button>
                    ))}
                  </div>
                  {shownMedia.length === 0 ? (
                    <span className="ddp-sim__target-empty">{t('sim.media.empty')}</span>
                  ) : (
                    <ul className="ddp-sim__media-list">
                      {shownMedia.map((m) => {
                        const sel = isMediaSelected(m.kind, m.id);
                        return (
                          <li key={`${m.kind}-${m.id}`}>
                            <button
                              type="button"
                              className={`ddp-sim__media-item${sel ? ' is-on' : ''}`}
                              aria-pressed={sel}
                              onClick={() => toggleMedia(m.kind, m.id)}
                            >
                              <span className="ddp-sim__media-kind">
                                {m.kind === 'VIDEO' ? '▶' : '▣'}
                              </span>
                              <span className="ddp-sim__media-name">{m.name}</span>
                              <span className="ddp-sim__media-sub">{m.sub}</span>
                              {sel && <span className="ddp-sim__media-check">✓</span>}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  <span className="ddp-sim__media-count">
                    {scenario.referenceMedia.length} {t('sim.media.selected')}
                  </span>
                </div>
              )}

              {/* Cibles : reprennent les labels du référentiel (FR-27). */}
              <div className="ddp-sim__targets">
                <span className="ddp-sim__label">{t('sim.field.targets')}</span>
                <div className="ddp-sim__target-chips">
                  {scenario.targets.map((tg) => (
                    <span key={tg.label} className="ddp-sim__target">
                      {tg.label}
                      <button
                        type="button"
                        aria-label={t('sim.removeTarget')}
                        onClick={() => removeTarget(tg.label)}
                      >
                        <Close size={14} />
                      </button>
                    </span>
                  ))}
                  {scenario.targets.length === 0 && (
                    <span className="ddp-sim__target-empty">{t('sim.noTarget')}</span>
                  )}
                </div>
                <div className="ddp-sim__target-add">
                  <input
                    className="ddp-sim__target-input"
                    list="sim-labels"
                    value={newTarget}
                    placeholder={t('sim.addTargetPlaceholder')}
                    onChange={(e) => setNewTarget(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addTarget(newTarget);
                      }
                    }}
                  />
                  <datalist id="sim-labels">
                    {labels.map((l) => (
                      <option key={l.id} value={l.name} />
                    ))}
                  </datalist>
                  <Button
                    kind="ghost"
                    size="sm"
                    renderIcon={Add}
                    onClick={() => addTarget(newTarget)}
                  >
                    {t('sim.addTarget')}
                  </Button>
                </div>
              </div>

              {/* Menaces aériennes : type figé (pilote le rendu) + label du référentiel (classe
                  apprise). Coexistent avec les cibles au sol (ADR-0023). */}
              <div className="ddp-sim__threats">
                <span className="ddp-sim__label">{t('sim.field.threats')}</span>
                <div className="ddp-sim__threat-presets">
                  {THREAT_PRESETS.map((p) => (
                    <button
                      type="button"
                      key={p.id}
                      className="ddp-sim__preset-chip"
                      onClick={() => addThreat(p.threat)}
                    >
                      ＋ {t(p.nameKey)}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="ddp-sim__preset-chip"
                    onClick={() => addThreat(BLANK_THREAT)}
                  >
                    ＋ {t('sim.threatPreset.blank')}
                  </button>
                </div>
                {scenario.threats.length === 0 ? (
                  <span className="ddp-sim__target-empty">{t('sim.noThreat')}</span>
                ) : (
                  scenario.threats.map((th, i) => (
                    <ThreatCard
                      key={i}
                      threat={th}
                      labels={labels}
                      t={t}
                      onChange={(p) => patchThreat(i, p)}
                      onRemove={() => removeThreat(i)}
                    />
                  ))
                )}
              </div>

              <div className="ddp-sim__outputs">
                <span className="ddp-sim__label">{t('sim.field.outputs')}</span>
                <Checkbox
                  id="sim-out-images"
                  labelText={t('sim.output.images')}
                  checked={scenario.outputs.labeledImages}
                  onChange={(_e, { checked }) =>
                    patch({ outputs: { ...scenario.outputs, labeledImages: checked } })
                  }
                />
                <Checkbox
                  id="sim-out-video"
                  labelText={t('sim.output.video')}
                  checked={scenario.outputs.video}
                  onChange={(_e, { checked }) =>
                    patch({ outputs: { ...scenario.outputs, video: checked } })
                  }
                />
              </div>
            </div>
          </div>

          {/* --- Volet droit : récapitulatif vivant --- */}
          <aside className="ddp-sim__recap">
            <h3 className="ddp-sim__recap-title">{t('sim.recap.title')}</h3>
            <div className="ddp-sim__preview">
              <ScenarioPreview scenario={scenario} refreshKey={backdropRefresh} />
            </div>
            <BackdropPicker
              environment={scenario.environment}
              environmentLabel={t(ENV_LABEL[scenario.environment])}
              images={images}
              canEdit={canEdit}
              onChanged={() => setBackdropRefresh((n) => n + 1)}
            />
            <dl className="ddp-sim__recap-list">
              <RecapRow
                k={t('sim.field.environment')}
                v={
                  reconstruction
                    ? `${t('sim.envSource.reconstruction')} · ${scenario.referenceMedia.length} ${t('sim.media.selected')}`
                    : t(ENV_LABEL[scenario.environment])
                }
              />
              <RecapRow
                k={t('sim.recap.timeSky')}
                v={`${t(TIME_LABEL[scenario.timeOfDay])} · ${t(SKY_LABEL[scenario.sky])}`}
              />
              <RecapRow
                k={t('sim.recap.altPitch')}
                v={`${scenario.altitudeM} m · ${scenario.gimbalPitchDeg}°`}
              />
              <RecapRow
                k={t('sim.field.targets')}
                v={
                  scenario.targets.length > 0
                    ? scenario.targets.map((tg) => `${tg.label} ×${tg.density}`).join(', ')
                    : '—'
                }
              />
              <RecapRow
                k={t('sim.field.threats')}
                v={
                  scenario.threats.length > 0
                    ? scenario.threats
                        .map((th) => `${t(THREAT_TYPE_LABEL[th.type])} ×${th.count}`)
                        .join(', ')
                    : '—'
                }
              />
              <RecapRow k={t('sim.field.outputs')} v={outputsSummary(scenario, t)} />
            </dl>
            <div className="ddp-sim__recap-actions">
              <Button size="md" disabled={!canCreate || busy} onClick={create}>
                {t('sim.create')}
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* --- Liste des lots --- */}
      <div className="ddp-sim__list-head">
        <h3 className="ddp-sim__subtitle">{t('sim.batches')}</h3>
      </div>
      {batches.length === 0 ? (
        <p className="ddp-sim__empty">{t('sim.noBatch')}</p>
      ) : (
        <ul className="ddp-sim__batches">
          {batches.map((b) => {
            const meta = STATUS_META[b.status];
            const launchable = b.status === 'DRAFT' || b.status === 'FAILED';
            return (
              <li key={b.id} className="ddp-sim__batch">
                <div className="ddp-sim__batch-body">
                  <div className="ddp-sim__batch-name">
                    {b.name}
                    <span className="ddp-sim__synthtag">{t('sim.synthetic')}</span>
                  </div>
                  <div className="ddp-sim__batch-meta">
                    {t(ENV_LABEL[b.scenario.environment])} · {t(TIME_LABEL[b.scenario.timeOfDay])} ·{' '}
                    {outputsSummary(b.scenario, t)}
                    {b.status === 'READY' && b.imageCount > 0 ? ` · ${b.imageCount} images` : ''}
                    {b.status === 'FAILED' && b.error ? ` · ${b.error}` : ''}
                  </div>
                  {b.status === 'APPROVED' && b.reviewedByName && (
                    <div className="ddp-sim__review ddp-sim__review--ok">
                      {t('sim.review.approvedBy')} {b.reviewedByName}
                    </div>
                  )}
                  {b.status === 'REJECTED' && (
                    <div className="ddp-sim__review ddp-sim__review--ko">
                      {t('sim.review.rejectedBy')} {b.reviewedByName ?? ''}
                      {b.rejectionReason ? ` — ${b.rejectionReason}` : ''}
                    </div>
                  )}
                </div>
                <span className={`ddp-sim__status ddp-sim__status--${meta.cls}`}>
                  <span className="ddp-sim__status-dot" aria-hidden="true" />
                  {t(meta.key)}
                </span>
                {canEdit && (
                  <div className="ddp-sim__batch-actions">
                    {launchable && (
                      <Button
                        kind="ghost"
                        size="sm"
                        renderIcon={Play}
                        iconDescription={t('sim.launch')}
                        hasIconOnly
                        onClick={() => launch(b.id)}
                      />
                    )}
                    {b.status === 'READY' && (
                      <>
                        <Button
                          kind="ghost"
                          size="sm"
                          renderIcon={Checkmark}
                          iconDescription={t('sim.review.approve')}
                          hasIconOnly
                          onClick={() => approve(b.id)}
                        />
                        <Button
                          kind="ghost"
                          size="sm"
                          renderIcon={CloseOutline}
                          iconDescription={t('sim.review.reject')}
                          hasIconOnly
                          onClick={() => setRejecting({ id: b.id, name: b.name })}
                        />
                      </>
                    )}
                    <Button
                      kind="ghost"
                      size="sm"
                      renderIcon={TrashCan}
                      iconDescription={t('sim.delete')}
                      hasIconOnly
                      onClick={() => remove(b.id)}
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <Modal
        open={rejecting !== null}
        danger
        modalHeading={t('sim.review.rejectHeading')}
        modalLabel={rejecting?.name}
        primaryButtonText={rejectBusy ? t('sim.review.rejecting') : t('sim.review.reject')}
        secondaryButtonText={t('sim.cancel')}
        primaryButtonDisabled={rejectReason.trim().length === 0 || rejectBusy}
        onRequestClose={() => {
          setRejecting(null);
          setRejectReason('');
        }}
        onRequestSubmit={() => void submitReject()}
      >
        <TextArea
          id="reject-reason"
          labelText={t('sim.review.reasonLabel')}
          placeholder={t('sim.review.reasonPlaceholder')}
          value={rejectReason}
          maxLength={500}
          onChange={(e) => setRejectReason(e.target.value)}
        />
      </Modal>
    </section>
  );
}

/**
 * Carte d'édition d'une menace aérienne (ADR-0023). Le `type` (figé) pilote le rendu ; le `label`
 * (repris du référentiel, « = type » par défaut) est la classe apprise. La cinématique décrit le
 * mouvement ; la dispersion n'apparaît que pour une trajectoire d'essaim.
 */
function ThreatCard({
  threat,
  labels,
  t,
  onChange,
  onRemove,
}: {
  threat: SimulationThreat;
  labels: Label[];
  t: (k: MessageKey) => string;
  onChange: (p: Partial<SimulationThreat>) => void;
  onRemove: () => void;
}) {
  const num = (raw: string | number, min: number, max: number, fallback: number): number =>
    clampNum(String(raw), min, max, fallback);

  return (
    <div className="ddp-sim__threat">
      <div className="ddp-sim__threat-hd">
        <span className="ddp-sim__threat-name">{t(THREAT_TYPE_LABEL[threat.type])}</span>
        <span className="ddp-sim__threat-count">×{threat.count}</span>
        <button
          type="button"
          className="ddp-sim__threat-rm"
          aria-label={t('sim.threat.remove')}
          onClick={onRemove}
        >
          <Close size={14} />
        </button>
      </div>
      <div className="ddp-sim__threat-fields">
        <Select
          id={`th-type`}
          size="sm"
          labelText={t('sim.threat.type')}
          value={threat.type}
          onChange={(e) => onChange({ type: e.target.value as SimulationThreatType })}
        >
          {SIMULATION_THREAT_TYPES.map((v) => (
            <SelectItem key={v} value={v} text={t(THREAT_TYPE_LABEL[v])} />
          ))}
        </Select>
        <Select
          id={`th-label`}
          size="sm"
          labelText={t('sim.threat.label')}
          value={threat.label ?? ''}
          onChange={(e) => onChange({ label: e.target.value === '' ? null : e.target.value })}
        >
          <SelectItem value="" text={t('sim.threat.labelDefault')} />
          {labels.map((l) => (
            <SelectItem key={l.id} value={l.name} text={l.name} />
          ))}
        </Select>
        <div className="ddp-sim__threat-num">
          <label>{t('sim.threat.size')}</label>
          <input
            type="number"
            min={1}
            max={60}
            value={threat.sizeInches ?? ''}
            placeholder="—"
            onChange={(e) =>
              onChange({
                sizeInches: e.target.value === '' ? null : num(e.target.value, 1, 60, 10),
              })
            }
          />
        </div>
        <div className="ddp-sim__threat-num">
          <label>{t('sim.threat.count')}</label>
          <input
            type="number"
            min={1}
            max={50}
            value={threat.count}
            onChange={(e) => onChange({ count: num(e.target.value, 1, 50, 1) })}
          />
        </div>
        <div className="ddp-sim__threat-num">
          <label>{t('sim.threat.speedMin')}</label>
          <input
            type="number"
            min={1}
            max={120}
            value={threat.speedMinMs}
            onChange={(e) => onChange({ speedMinMs: num(e.target.value, 1, 120, 10) })}
          />
        </div>
        <div className="ddp-sim__threat-num">
          <label>{t('sim.threat.speedMax')}</label>
          <input
            type="number"
            min={1}
            max={120}
            value={threat.speedMaxMs}
            onChange={(e) => onChange({ speedMaxMs: num(e.target.value, 1, 120, 20) })}
          />
        </div>
        <Select
          id={`th-approach`}
          size="sm"
          labelText={t('sim.threat.approach')}
          value={threat.approach}
          onChange={(e) => onChange({ approach: e.target.value as SimulationThreatApproach })}
        >
          {SIMULATION_THREAT_APPROACHES.map((v) => (
            <SelectItem key={v} value={v} text={t(THREAT_APPROACH_LABEL[v])} />
          ))}
        </Select>
        <Select
          id={`th-traj`}
          size="sm"
          labelText={t('sim.threat.trajectory')}
          value={threat.trajectory}
          onChange={(e) => onChange({ trajectory: e.target.value as SimulationThreatTrajectory })}
        >
          {SIMULATION_THREAT_TRAJECTORIES.map((v) => (
            <SelectItem key={v} value={v} text={t(THREAT_TRAJ_LABEL[v])} />
          ))}
        </Select>
        {threat.trajectory === 'DISPERSE' && (
          <div className="ddp-sim__threat-num">
            <label>{t('sim.threat.dispersion')}</label>
            <input
              type="number"
              min={0}
              max={180}
              value={threat.dispersionDeg ?? ''}
              placeholder="—"
              onChange={(e) =>
                onChange({
                  dispersionDeg: e.target.value === '' ? null : num(e.target.value, 0, 180, 90),
                })
              }
            />
          </div>
        )}
      </div>
    </div>
  );
}

function RecapRow({ k, v }: { k: string; v: string }) {
  return (
    <div className="ddp-sim__recap-row">
      <dt>{k}</dt>
      <dd>{v}</dd>
    </div>
  );
}

/** Résumé lisible des sorties demandées (images, vidéo, ou les deux). */
function outputsSummary(s: SimulationScenario, t: (k: MessageKey) => string): string {
  const parts: string[] = [];
  if (s.outputs.labeledImages) parts.push(`${s.imageCount} ${t('sim.output.imagesShort')}`);
  if (s.outputs.video) parts.push(`${s.videoDurationSeconds}s ${t('sim.output.videoShort')}`);
  return parts.join(' + ') || '—';
}

/**
 * Assistant inline (ADR-0016 + ADR-0021) : depuis une description libre, l'assistant **propose un
 * scénario** — en s'appuyant sur les labels réels de l'organisation (outil `list_labels`) — qui
 * **remplit le formulaire**. L'utilisateur ajuste ensuite. Dormant tant que l'assistant n'est pas
 * activé (503) ; une réponse inexploitable renvoie un 422 affiché tel quel.
 */
function ScenarioAssistant({ onApply }: { onApply: (scenario: SimulationScenario) => void }) {
  const t = useT();
  // On ne se sert de `useAssistant` que pour l'état (actif / dormant), affiché sans tenter d'envoyer.
  const { status } = useAssistant();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (status && !status.enabled) {
    return (
      <div className="ddp-sim__assistant ddp-sim__assistant--off">{t('sim.assistantOff')}</div>
    );
  }

  const submit = async () => {
    const trimmed = text.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      const reply = await apiFetch<SimulationAssistReply>('/simulation/batches/assist', {
        method: 'POST',
        body: { description: trimmed },
      });
      onApply(reply.scenario);
      setText('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) setError(t('sim.assistantOff'));
      else setError(err instanceof Error ? err.message : t('sim.assistError'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ddp-sim__assistant">
      <div className="ddp-sim__assistant-hint">{t('sim.assistantHint')}</div>
      {error && <div className="ddp-sim__assistant-err">{error}</div>}
      <div className="ddp-sim__assistant-input">
        <input
          value={text}
          placeholder={t('sim.assistantPlaceholder')}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void submit();
            }
          }}
        />
        <Button
          kind="ghost"
          size="sm"
          renderIcon={Send as ComponentType}
          iconDescription={t('sim.assistantSend')}
          hasIconOnly
          disabled={busy}
          onClick={() => void submit()}
        />
      </div>
    </div>
  );
}
